import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Per-route <head> metadata for a client-rendered SPA.
 *
 * index.html is the single shell every route is served from, so the canonical
 * link it carries pointed every URL at the home page. Google renders the page
 * before it reads rel=canonical, believed all of them, and filed /search,
 * /coffee/:id and the rest as "Alternate page with proper canonical tag" —
 * duplicates of `/`, not indexed. Writing the tags per route from the rendered
 * DOM is what Googlebot ends up reading, so it is enough to undo that.
 *
 * Rendering the shell per route on the edge would put the same values in the
 * response body rather than in the rendered DOM. That is the sturdier fix and
 * would also give social crawlers, which do not run JavaScript, the right card
 * — but it needs a Pages Function in front of the static build.
 */

export const SITE_ORIGIN = 'https://signaturecoffeenote.com';

export const DEFAULT_TITLE = 'Signature Coffee Note';
export const DEFAULT_IMAGE = `${SITE_ORIGIN}/social-card.jpg`;

export const DEFAULT_DESCRIPTION =
  "Discover and review signature coffees — the one-shop-only creations you can't drink anywhere else.";

// Kept in sync with the routes in src/App.jsx and with public/sitemap.xml.
const STATIC_ROUTES = {
  '/': {
    title: 'Signature Coffee Note — Discover signature coffees',
    description: DEFAULT_DESCRIPTION,
  },
  '/search': {
    title: 'Search signature coffees | Signature Coffee Note',
    description:
      'Search signature coffees by name, cafe, city, brew method or bean origin.',
  },
  '/privacy': {
    title: 'Privacy Policy | Signature Coffee Note',
    description: 'How Signature Coffee Note collects, uses and protects your data.',
  },
  '/terms': {
    title: 'Terms of Service | Signature Coffee Note',
    description: 'The terms that apply when you use Signature Coffee Note.',
  },
  '/contact': {
    title: 'Contact | Signature Coffee Note',
    description: 'Get in touch with the Signature Coffee Note team.',
  },
};

// Signed-out visitors — Googlebot included — are bounced from the protected
// routes to /login, which Search Console reports as "Page with redirect". The
// auth pages themselves carry nothing worth a search result. Neither belongs
// in the index, and a URL we ask not to be indexed must not claim a canonical.
const NOINDEX_ROUTES = new Set([
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/add',
  '/my',
  '/admin',
]);

// Routes whose path carries an id. Indexable, and each one canonical to
// itself; the page fills in its own title once the record has loaded.
const DYNAMIC_PREFIXES = ['/coffee/', '/user/'];

/** Strips the trailing slash (except on the root) so a path maps to one URL. */
export function normalizePath(pathname) {
  if (!pathname || pathname === '/') return '/';
  return pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
}

/**
 * Metadata for a path, before the page refines it.
 * `canonical: null` means "emit no canonical link" — used wherever the URL is
 * not one we want indexed.
 */
export function metaForPath(pathname) {
  const path = normalizePath(pathname);

  const staticMeta = STATIC_ROUTES[path];
  if (staticMeta) {
    return { ...staticMeta, canonical: SITE_ORIGIN + path, index: true };
  }

  if (NOINDEX_ROUTES.has(path)) {
    return { title: DEFAULT_TITLE, description: DEFAULT_DESCRIPTION, canonical: null, index: false };
  }

  const isDynamic = DYNAMIC_PREFIXES.some(
    (prefix) => path.startsWith(prefix) && path.length > prefix.length,
  );
  if (isDynamic) {
    return {
      title: DEFAULT_TITLE,
      description: DEFAULT_DESCRIPTION,
      canonical: SITE_ORIGIN + path,
      index: true,
    };
  }

  // Anything left is the 404 route. The SPA rewrite answers it with a 200, so
  // noindex is the only signal available to keep it out of the index.
  return {
    title: 'Page Not Found | Signature Coffee Note',
    description: DEFAULT_DESCRIPTION,
    canonical: null,
    index: false,
  };
}

function upsertTag(selector, create) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  return el;
}

function setMetaContent(attr, name, content) {
  const selector = `meta[${attr}="${name}"]`;
  if (!content) {
    document.head.querySelector(selector)?.remove();
    return;
  }
  const el = upsertTag(selector, () => {
    const tag = document.createElement('meta');
    tag.setAttribute(attr, name);
    return tag;
  });
  el.setAttribute('content', content);
}

function setLinkHref(rel, href) {
  const selector = `link[rel="${rel}"]`;
  if (!href) {
    document.head.querySelector(selector)?.remove();
    return;
  }
  const el = upsertTag(selector, () => {
    const tag = document.createElement('link');
    tag.setAttribute('rel', rel);
    return tag;
  });
  el.setAttribute('href', href);
}

/** Absolute http(s) URLs only — a relative one would break the social card. */
function absoluteImage(image) {
  return typeof image === 'string' && /^https?:\/\//.test(image) ? image : null;
}

export function applyPageMeta({ title, description, canonical, index, image }) {
  if (typeof document === 'undefined') return;

  // A URL we ask not to be indexed must not nominate itself as canonical:
  // the two together are contradictory, and Search Console reports the
  // conflict rather than honouring either.
  const href = index ? canonical : null;

  document.title = title || DEFAULT_TITLE;
  setMetaContent('name', 'description', description || DEFAULT_DESCRIPTION);
  setLinkHref('canonical', href);
  setMetaContent('property', 'og:url', href);
  setMetaContent('property', 'og:title', title || DEFAULT_TITLE);
  setMetaContent('property', 'og:description', description || DEFAULT_DESCRIPTION);
  setMetaContent('name', 'twitter:title', title || DEFAULT_TITLE);
  setMetaContent('name', 'twitter:description', description || DEFAULT_DESCRIPTION);
  setMetaContent('name', 'robots', index ? null : 'noindex, follow');

  // Always written, never merely overwritten: an in-app navigation away from a
  // coffee page would otherwise leave its photo on the next page's card.
  const ogImage = absoluteImage(image) || DEFAULT_IMAGE;
  setMetaContent('property', 'og:image', ogImage);
  setMetaContent('name', 'twitter:image', ogImage);
}

/**
 * Refines the current route's metadata once a page has its data — the coffee's
 * name, the member's display name. Pass undefined for anything still loading
 * and the route default stays in place. `index: false` additionally drops the
 * page out of the index, which is how a record that does not exist avoids
 * being indexed as a soft 404.
 *
 * Safe to call from a route that RouteMeta has already handled: this runs
 * after it on every navigation, because effects fire in tree order and
 * RouteMeta is mounted above the routes.
 *
 * @param {{ title?: string, description?: string, image?: string, index?: boolean }} [meta]
 */
export function usePageMeta({ title, description, image, index } = {}) {
  const { pathname } = useLocation();

  useEffect(() => {
    const base = metaForPath(pathname);
    applyPageMeta({
      ...base,
      title: title || base.title,
      description: description || base.description,
      index: index === undefined ? base.index : index,
      image,
    });
  }, [pathname, title, description, image, index]);
}
